/**
 * This module defines custom PropTypes shared within the application.
 *
 * To learn about validating React component props with PropTypes, see:
 *
 *     https://facebook.github.io/react/docs/typechecking-with-proptypes.html
 *
 * For component specific PropTypes, it's perfectly ok to inline them
 * to the component itself. If the type is shared or external (SDK or
 * API), however, it should be in this file for sharing with other
 * components.
 *
 * PropTypes should usually be validated only at the lowest level
 * where the props are used, not along the way in parents that pass
 * along the props to their children. Those parents should usually
 * just validate the presense of the prop key and that the value is
 * defined. This way we get the validation errors only in the most
 * specific place and avoid duplicate errros.
 */
import React, { Component } from 'react';
import {
  arrayOf,
  bool,
  func,
  instanceOf,
  node,
  number,
  object,
  objectOf,
  oneOf,
  oneOfType,
  shape,
  string,
} from 'prop-types';
import Decimal from 'decimal.js';
import { types as sdkTypes } from './sdkLoader';
import {
  getAllTransitionsForEveryProcess,
  TX_TRANSITION_ACTORS,
} from '../transactions/transaction';
import ProfileIconCard from '../components/ProfileIconCard/ProfileIconCard';

const {
  REACT_APP_ALGOLIA_LISTING_INDEX,
  REACT_APP_ALGOLIA_LISTING_CREATED_INDEX,
  REACT_APP_ALGOLIA_LISTING_PRICE_ASC_INDEX,
  REACT_APP_ALGOLIA_LISTING_PRICE_DESC_INDEX,
  REACT_APP_ALGOLIA_LISTING_OLD_CREATED_INDEX
} = process.env;

// NOTE: This file imports ../transactions/transaction.js, which may lead to circular dependency

const { UUID, LatLng, LatLngBounds, Money } = sdkTypes;
const TRANSITIONS = getAllTransitionsForEveryProcess();

// Supported schema types for custom fields added to extended data through configuration.
export const SCHEMA_TYPE_ENUM = 'enum';
export const SCHEMA_TYPE_MULTI_ENUM = 'multi-enum';
export const SCHEMA_TYPE_TEXT = 'text';
export const SCHEMA_TYPE_LONG = 'long';
export const SCHEMA_TYPE_BOOLEAN = 'boolean';
export const SCHEMA_TYPE_YOUTUBE = 'youtubeVideoUrl';
export const SCHEMA_TYPE_ADDRESS = 'address';
export const SCHEMA_TYPE_SHIPPING_ADDRESS = 'shipping-address';
export const SCHEMA_TYPE_MULTI_SELECT_CREATE_ENUM = 'multi-select-create-enum';

export const EXTENDED_DATA_SCHEMA_TYPES = [
  SCHEMA_TYPE_ENUM,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_LONG,
  SCHEMA_TYPE_BOOLEAN,
  SCHEMA_TYPE_YOUTUBE,
  SCHEMA_TYPE_ADDRESS,
  SCHEMA_TYPE_MULTI_SELECT_CREATE_ENUM,
];

// Listing Page tabs
export const LISTING_PAGE_DESCRIPTION_TAB = 'Description';
export const LISTING_PAGE_REVIEW_TAB = 'Seller Reviews';
export const LISTING_PAGE_TABS = [LISTING_PAGE_DESCRIPTION_TAB, LISTING_PAGE_REVIEW_TAB];

// OFFER INQUIREY TAB
export const LISTING_PAGE_OFFER_INQUIREY = 'offer';

export const LISTING_PAGE_DETAILS_FIELDS = [
  {
    "key": 'categoryName',
    "label": 'Category',
  },
  {
    "key": 'subCategoryName',
    "label": 'Category',
  },
  {
    "key": 'subSubCategoryName',
    "label": 'Sub Category',
  },
  {
    "key": "typeName",
    "label": "Type"
  },
  {
    "key": 'condition',
    "label": 'Condition',
  },
  {
    "key": "brandName",
    "label": "Brand"
  },
  {
    "key": "sizeName",
    "label": "Size"
  },
  {
    "key": "size_in_inchesName",
    "label": "Size In Inches"
  },
  {
    "key": "blanket_sizeName",
    "label": "Blanket Size"
  },
  {
    "key": "numeric_sizeName",
    "label": "Numeric Size"
  },
  {
    "key": "chest_sizeName",
    "label": "Chest Size"
  },
  {
    "key": "seat_sizeName",
    "label": "Seat Size"
  },
  {
    "key": "waist_sizeName",
    "label": "Waist Size"
  },
  {
    "key": "waist_in_inchesName",
    "label": "Waist In Inches"
  },
  {
    "key": "waistName",
    "label": "Waist"
  },
  {
    "key": "colorName",
    "label": "Color"
  },
  {
    "key": "weightName",
    "label": "Weight"
  },
  {
    "key": "blanket_weightName",
    "label": "Blanket Weight"
  },
  {
    "key": "materialName",
    "label": "Material"
  },
  {
    "key": "lengthName",
    "label": "Length"
  },
  {
    "key": "attachmentName",
    "label": "Attachment"
  },
  {
    "key": "formName",
    "label": "Form"
  },
  {
    "key": "styleName",
    "label": "Style"
  },
  {
    "key": "thicknessName",
    "label": "Thickness"
  },
  {
    "key": "disciplineName",
    "label": "Discipline"
  },
  {
    "key": "englishName",
    "label": "English"
  },
  {
    "key": "westernName",
    "label": "Western"
  },
  {
    "key": "genderName",
    "label": "Gender"
  },
  {
    "key": "stall_typeName",
    "label": "Stall Type"
  },
  {
    "key": "living_quartersName",
    "label": "Living Quarters"
  }
];

const propTypes = {};

// Fixed value
propTypes.value = val => oneOf([val]);

// SDK type instances
propTypes.uuid = instanceOf(UUID);
propTypes.latlng = instanceOf(LatLng);
propTypes.latlngBounds = instanceOf(LatLngBounds);
propTypes.money = instanceOf(Money);

// Configuration for currency formatting
propTypes.currencyConfig = shape({
  style: string.isRequired,
  currency: string.isRequired,
  currencyDisplay: string,
  useGrouping: bool,
  minimumFractionDigits: number,
  maximumFractionDigits: number,
});

// Configuration for a single route
propTypes.route = shape({
  name: string.isRequired,
  path: string.isRequired,
  exact: bool,
  strict: bool,
  component: oneOfType([object, func]).isRequired,
  loadData: func,
});

// Place object from LocationAutocompleteInput
propTypes.place = shape({
  address: string.isRequired,
  origin: propTypes.latlng,
  bounds: propTypes.latlngBounds, // optional viewport bounds
});

// Denormalised image object
propTypes.image = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('image').isRequired,
  attributes: shape({
    variants: objectOf(
      shape({
        width: number.isRequired,
        height: number.isRequired,
        url: string.isRequired,
      })
    ),
  }),
});

// ImageAsset type from Asset Delivery API
propTypes.imageAsset = shape({
  id: string.isRequired,
  type: propTypes.value('imageAsset').isRequired,
  attributes: shape({
    variants: objectOf(
      shape({
        width: number.isRequired,
        height: number.isRequired,
        url: string.isRequired,
      })
    ),
  }),
});

// Denormalised user object
const currentUser = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('currentUser').isRequired,
  attributes: shape({
    banned: bool.isRequired,
    email: string.isRequired,
    emailVerified: bool.isRequired,
    profile: shape({
      firstName: string.isRequired,
      lastName: string.isRequired,
      displayName: string.isRequired,
      abbreviatedName: string.isRequired,
      bio: string,
    }).isRequired,
    stripeConnected: bool,
  }),
  profileImage: propTypes.image,
});
const currentUserBanned = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('currentUser').isRequired,
  attributes: shape({
    banned: propTypes.value(true).isRequired,
  }),
});
const currentUserDeleted = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('currentUser').isRequired,
  attributes: shape({
    deleted: propTypes.value(true).isRequired,
  }),
});
propTypes.currentUser = oneOfType([currentUser, currentUserBanned, currentUserDeleted]);

const userAttributes = shape({
  banned: propTypes.value(false).isRequired,
  deleted: propTypes.value(false).isRequired,
  profile: shape({
    displayName: string.isRequired,
    abbreviatedName: string.isRequired,
    bio: string,
  }),
});

// Listing queries can include author.
// Banned and deleted are not relevant then
// since banned and deleted users can't have listings.
const authorAttributes = shape({
  profile: shape({
    displayName: string.isRequired,
    abbreviatedName: string.isRequired,
    bio: string,
  }),
});

const deletedUserAttributes = shape({
  deleted: propTypes.value(true).isRequired,
});

const bannedUserAttributes = shape({
  banned: propTypes.value(true).isRequired,
});

// Denormalised user object
propTypes.user = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('user').isRequired,
  attributes: oneOfType([
    userAttributes,
    authorAttributes,
    deletedUserAttributes,
    bannedUserAttributes,
  ]).isRequired,
  profileImage: propTypes.image,
});

export const LISTING_STATE_DRAFT = 'draft';
export const LISTING_STATE_PENDING_APPROVAL = 'pendingApproval';
export const LISTING_STATE_PUBLISHED = 'published';
export const LISTING_STATE_CLOSED = 'closed';

const LISTING_STATES = [
  LISTING_STATE_DRAFT,
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_PUBLISHED,
  LISTING_STATE_CLOSED,
];

const listingAttributes = shape({
  title: string.isRequired,
  description: string,
  geolocation: propTypes.latlng,
  deleted: propTypes.value(false),
  state: oneOf(LISTING_STATES),
  price: propTypes.money,
  publicData: object,
});

const AVAILABILITY_PLAN_DAY = 'availability-plan/day';
const AVAILABILITY_PLAN_TIME = 'availability-plan/time';
export const DAYS_OF_WEEK = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const availabilityPlan = shape({
  type: oneOf([AVAILABILITY_PLAN_DAY, AVAILABILITY_PLAN_TIME]).isRequired,
  timezone: string,
  entries: arrayOf(
    shape({
      dayOfWeek: oneOf(DAYS_OF_WEEK).isRequired,
      seats: number.isRequired,
      start: string,
      end: string,
    })
  ),
});

propTypes.availabilityPlan = availabilityPlan;

const ownListingAttributes = shape({
  title: string.isRequired,
  description: string,
  geolocation: propTypes.latlng,
  deleted: propTypes.value(false).isRequired,
  state: oneOf(LISTING_STATES).isRequired,
  price: propTypes.money,
  availabilityPlan: availabilityPlan,
  publicData: object.isRequired,
});

const deletedListingAttributes = shape({
  deleted: propTypes.value(true).isRequired,
});

// Denormalised listing object
propTypes.listing = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('listing').isRequired,
  attributes: oneOfType([listingAttributes, deletedListingAttributes]).isRequired,
  author: propTypes.user,
  images: arrayOf(propTypes.image),
});

// Denormalised ownListing object
propTypes.ownListing = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('ownListing').isRequired,
  attributes: oneOfType([ownListingAttributes, deletedListingAttributes]).isRequired,
  author: propTypes.currentUser,
  images: arrayOf(propTypes.image),
});

export const BOOKING_STATE_PENDING = 'pending';
export const BOOKING_STATE_ACCEPTED = 'accepted';
export const BOOKING_STATE_DECLINED = 'declined';
export const BOOKING_STATE_CANCELLED = 'cancelled';
export const BOOKING_STATES = [
  BOOKING_STATE_PENDING,
  BOOKING_STATE_ACCEPTED,
  BOOKING_STATE_DECLINED,
  BOOKING_STATE_CANCELLED,
];

// Denormalised booking object
propTypes.booking = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('booking').isRequired,
  attributes: shape({
    end: instanceOf(Date).isRequired,
    start: instanceOf(Date).isRequired,
    displayStart: instanceOf(Date),
    displayEnd: instanceOf(Date),
    state: oneOf(BOOKING_STATES),
  }),
});

// A time slot that covers one day, having a start and end date.
export const TIME_SLOT_TIME = 'time-slot/time';

// Denormalised time slot object
propTypes.timeSlot = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('timeSlot').isRequired,
  attributes: shape({
    type: oneOf([TIME_SLOT_TIME]).isRequired,
    end: instanceOf(Date).isRequired,
    start: instanceOf(Date).isRequired,
  }),
});

// Denormalised availability exception object
propTypes.availabilityException = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('availabilityException').isRequired,
  attributes: shape({
    end: instanceOf(Date).isRequired,
    seats: number.isRequired,
    start: instanceOf(Date).isRequired,
  }),
});

export const STOCK_ONE_ITEM = 'oneItem';
export const STOCK_MULTIPLE_ITEMS = 'multipleItems';
export const STOCK_INFINITE_ONE_ITEM = 'infiniteOneItem';
export const STOCK_INFINITE_MULTIPLE_ITEMS = 'infiniteMultipleItems';
export const STOCK_INFINITE_ITEMS = [STOCK_INFINITE_ONE_ITEM, STOCK_INFINITE_MULTIPLE_ITEMS];
export const STOCK_TYPES = [
  STOCK_ONE_ITEM,
  STOCK_MULTIPLE_ITEMS,
  STOCK_INFINITE_ONE_ITEM,
  STOCK_INFINITE_MULTIPLE_ITEMS,
];

// Inventory type options for listing creation
export const INVENTORY_TYPE_SINGLE = 'single';
export const INVENTORY_TYPE_VARIANTS = 'variants';

export const inventoryTypeOptions = [
  {
    key: INVENTORY_TYPE_SINGLE,
    label: 'One item or multiple identical items',
    value: INVENTORY_TYPE_SINGLE,
  },
  {
    key: INVENTORY_TYPE_VARIANTS,
    label: 'Multiple versions of the same item (e.g., different color/size)',
    value: INVENTORY_TYPE_VARIANTS,
  },
];

propTypes.transition = shape({
  createdAt: instanceOf(Date).isRequired,
  by: oneOf(TX_TRANSITION_ACTORS).isRequired,
  transition: oneOf(TRANSITIONS).isRequired,
});

// Possible amount of stars in a review
export const REVIEW_RATINGS = [1, 2, 3, 4, 5];

// Review types: review of a provider and of a customer
export const REVIEW_TYPE_OF_PROVIDER = 'ofProvider';
export const REVIEW_TYPE_OF_CUSTOMER = 'ofCustomer';

// A review on a user
propTypes.review = shape({
  id: propTypes.uuid.isRequired,
  attributes: shape({
    createdAt: instanceOf(Date).isRequired,
    content: string,
    rating: oneOf(REVIEW_RATINGS),
    state: string.isRequired,
    type: oneOf([REVIEW_TYPE_OF_PROVIDER, REVIEW_TYPE_OF_CUSTOMER]).isRequired,
  }).isRequired,
  author: propTypes.user,
  subject: propTypes.user,
});

// A Stripe account entity
propTypes.stripeAccount = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('stripeAccount').isRequired,
  attributes: shape({
    stripeAccountId: string.isRequired,
    stripeAccountData: object,
  }),
});

propTypes.defaultPaymentMethod = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('stripePaymentMethod').isRequired,
  attributes: shape({
    type: propTypes.value('stripe-payment-method/card').isRequired,
    stripePaymentMethodId: string.isRequired,
    card: shape({
      brand: string.isRequired,
      expirationMonth: number.isRequired,
      expirationYear: number.isRequired,
      last4Digits: string.isRequired,
    }).isRequired,
  }),
});

export const LINE_ITEM_NIGHT = 'line-item/night';
export const LINE_ITEM_DAY = 'line-item/day';
export const LINE_ITEM_HOUR = 'line-item/hour';
export const LINE_ITEM_ITEM = 'line-item/item';
export const LINE_ITEM_CUSTOMER_COMMISSION = 'line-item/customer-commission';
export const LINE_ITEM_PROVIDER_COMMISSION = 'line-item/provider-commission';
export const LINE_ITEM_SHIPPING_FEE = 'line-item/shipping-fee';
export const LINE_ITEM_PICKUP_FEE = 'line-item/pickup-fee';

export const LINE_ITEMS = [
  LINE_ITEM_NIGHT,
  LINE_ITEM_DAY,
  LINE_ITEM_HOUR,
  LINE_ITEM_ITEM,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_PROVIDER_COMMISSION,
  LINE_ITEM_SHIPPING_FEE,
  LINE_ITEM_PICKUP_FEE,
];
export const LISTING_UNIT_TYPES = [LINE_ITEM_NIGHT, LINE_ITEM_DAY, LINE_ITEM_HOUR, LINE_ITEM_ITEM];

propTypes.lineItemUnitType = oneOf(LISTING_UNIT_TYPES);

const requiredLineItemPropType = (props, propName, componentName) => {
  const prop = props[propName];

  if (!prop || prop === '') {
    return new Error(`Missing line item code prop from ${componentName}.`);
  }
  if (!/^line-item\/.+/.test(prop)) {
    return new Error(`Invalid line item code value ${prop} passed to ${componentName}.`);
  }
};

propTypes.lineItems = arrayOf(
  shape({
    code: requiredLineItemPropType,
    includeFor: arrayOf(oneOf(['customer', 'provider'])).isRequired,
    quantity: instanceOf(Decimal),
    unitPrice: propTypes.money.isRequired,
    lineTotal: propTypes.money.isRequired,
    reversal: bool.isRequired,
  })
);

// Denormalised transaction object
propTypes.transaction = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('transaction').isRequired,
  attributes: shape({
    createdAt: instanceOf(Date),
    processName: string.isRequired,
    lastTransitionedAt: instanceOf(Date).isRequired,
    lastTransition: oneOf(TRANSITIONS).isRequired,

    // An inquiry won't need a total sum nor a booking so these are
    // optional.
    payinTotal: propTypes.money,
    payoutTotal: propTypes.money,

    lineItems: propTypes.lineItems,
    transitions: arrayOf(propTypes.transition).isRequired,
  }),
  booking: propTypes.booking,
  listing: propTypes.listing,
  customer: propTypes.user,
  provider: propTypes.user,
  reviews: arrayOf(propTypes.review),
});

// Denormalised transaction message
propTypes.message = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('message').isRequired,
  attributes: shape({
    createdAt: instanceOf(Date).isRequired,
    content: string.isRequired,
  }).isRequired,
  sender: propTypes.user,
});

// Pagination information in the response meta
propTypes.pagination = shape({
  page: number.isRequired,
  perPage: number.isRequired,
  totalItems: number,
  totalPages: number,
});

// Search filter definition
propTypes.filterConfig = arrayOf(
  shape({
    id: string.isRequired,
    label: node,
    type: string.isRequired,
    group: oneOf(['primary', 'secondary']).isRequired,
    queryParamNames: arrayOf(string).isRequired,
    config: object,
  }).isRequired
);

// Default search filters definition
propTypes.defaultFiltersConfig = arrayOf(
  shape({
    key: string.isRequired,
    schemaType: oneOf(['price', 'text', 'dates']).isRequired,
    min: number,
    max: number,
    step: number,
  }).isRequired
);

// Extended data config
propTypes.userType = shape({
  userType: string.isRequired,
  label: string.isRequired,
  defaultUserFields: shape({
    displayName: bool,
    phoneNumber: bool,
  }),
  displayNameSettings: shape({
    displayInSignUp: bool,
    required: bool,
  }),
  phoneNumberSettings: shape({
    displayInSignUp: bool,
    required: bool,
  }),
});
propTypes.userTypes = arrayOf(propTypes.userType);

propTypes.fieldEnumOptions = arrayOf(
  shape({
    option: oneOfType([string, number]).isRequired,
    label: string.isRequired,
  })
);

propTypes.userField = shape({
  key: string.isRequired,
  scope: string,
  schemaType: oneOf(EXTENDED_DATA_SCHEMA_TYPES).isRequired,
  enumOptions: propTypes.fieldEnumOptions,
  showConfig: shape({
    label: string.isRequired,
    displayInProfile: bool,
  }),
  saveConfig: shape({
    label: string.isRequired,
    placeholderMessage: string,
    isRequired: bool,
    requiredMessage: string,
    displayInSignUp: bool,
  }).isRequired,
  userTypeConfig: shape({
    limitToUserTypeIds: bool.isRequired,
    userTypeIds: arrayOf(string),
  }),
});
propTypes.userFields = arrayOf(propTypes.userField);

propTypes.listingType = shape({
  listingType: string.isRequired,
  label: string.isRequired,
  transactionType: shape({
    process: string.isRequired,
    alias: string.isRequired,
    unitType: string.isRequired,
  }).isRequired,
  defaultListingFields: shape({
    price: bool,
    location: bool,
    payoutDetails: bool,
    shipping: bool,
    pickup: bool,
  }),
});
propTypes.listingTypes = arrayOf(propTypes.userType);

propTypes.listingField = shape({
  key: string.isRequired,
  scope: string,
  schemaType: oneOf(EXTENDED_DATA_SCHEMA_TYPES).isRequired,
  enumOptions: propTypes.fieldEnumOptions,
  filterConfig: shape({
    indexForSearch: bool,
    label: string.isRequired,
    group: oneOf(['primary', 'secondary']),
    filterType: string,
  }),
  showConfig: shape({
    label: string.isRequired,
    isDetail: bool,
  }),
  saveConfig: shape({
    label: string.isRequired,
    placeholderMessage: string,
    isRequired: bool,
    requiredMessage: string,
  }).isRequired,
  listingTypeConfig: shape({
    limitToListingTypeIds: bool.isRequired,
    listingTypeIds: arrayOf(string),
  }),
  categoryConfig: shape({
    limitToCategoryIds: bool.isRequired,
    categoryIds: arrayOf(string),
  }),
});

propTypes.listingFields = arrayOf(propTypes.listingField);

const sortConfigOptionWithLabel = shape({
  key: oneOf(['createdAt', '-createdAt', 'price', '-price', 'relevance']).isRequired,
  label: string.isRequired,
  longLabel: string,
});

const sortConfigOptionWithTranslationKey = shape({
  key: oneOf(['createdAt', '-createdAt', 'price', '-price', 'relevance']).isRequired,
  labelTranslationKey: string.isRequired,
  labelTranslationKeyLong: string,
});

propTypes.sortConfig = shape({
  active: bool,
  queryParamName: oneOf(['sort']).isRequired,
  relevanceKey: string.isRequired,
  conflictingFilters: arrayOf(string),
  options: arrayOf(oneOfType([sortConfigOptionWithLabel, sortConfigOptionWithTranslationKey])),
});

export const ERROR_CODE_TRANSACTION_LISTING_NOT_FOUND = 'transaction-listing-not-found';
export const ERROR_CODE_TRANSACTION_INVALID_TRANSITION = 'transaction-invalid-transition';
export const ERROR_CODE_TRANSACTION_ALREADY_REVIEWED_BY_CUSTOMER =
  'transaction-already-reviewed-by-customer';
export const ERROR_CODE_TRANSACTION_ALREADY_REVIEWED_BY_PROVIDER =
  'transaction-already-reviewed-by-provider';
export const ERROR_CODE_TRANSACTION_BOOKING_TIME_NOT_AVAILABLE =
  'transaction-booking-time-not-available';
export const ERROR_CODE_TRANSACTION_LISTING_INSUFFICIENT_STOCK =
  'transaction-listing-insufficient-stock';
export const ERROR_CODE_PAYMENT_FAILED = 'transaction-payment-failed';
export const ERROR_CODE_CHARGE_ZERO_PAYIN = 'transaction-charge-zero-payin';
export const ERROR_CODE_CHARGE_ZERO_PAYOUT = 'transaction-charge-zero-payout';
export const ERROR_CODE_EMAIL_TAKEN = 'email-taken';
export const ERROR_CODE_EMAIL_NOT_FOUND = 'email-not-found';
export const ERROR_CODE_TOO_MANY_VERIFICATION_REQUESTS = 'email-too-many-verification-requests';
export const ERROR_CODE_UPLOAD_OVER_LIMIT = 'request-upload-over-limit';
export const ERROR_CODE_VALIDATION_INVALID_PARAMS = 'validation-invalid-params';
export const ERROR_CODE_VALIDATION_INVALID_VALUE = 'validation-invalid-value';
export const ERROR_CODE_NOT_FOUND = 'not-found';
export const ERROR_CODE_FORBIDDEN = 'forbidden';
export const ERROR_CODE_MISSING_STRIPE_ACCOUNT = 'transaction-missing-stripe-account';
export const ERROR_CODE_STOCK_OLD_TOTAL_MISMATCH = 'old-total-mismatch';
export const ERROR_CODE_PERMISSION_DENIED_POST_LISTINGS = 'permission-denied-post-listings';
export const ERROR_CODE_PERMISSION_DENIED_PENDING_APPROVAL = 'permission-denied-pending-approval';
export const ERROR_CODE_USER_PENDING_APPROVAL = 'user-pending-approval';
export const ERROR_CODE_PERMISSION_DENIED_INITIATE_TRANSACTIONS =
  'permission-denied-initiate-transactions';
export const ERROR_CODE_PERMISSION_DENIED_READ = 'permission-denied-read';

const ERROR_CODES = [
  ERROR_CODE_TRANSACTION_LISTING_NOT_FOUND,
  ERROR_CODE_TRANSACTION_INVALID_TRANSITION,
  ERROR_CODE_TRANSACTION_ALREADY_REVIEWED_BY_CUSTOMER,
  ERROR_CODE_TRANSACTION_ALREADY_REVIEWED_BY_PROVIDER,
  ERROR_CODE_PAYMENT_FAILED,
  ERROR_CODE_CHARGE_ZERO_PAYIN,
  ERROR_CODE_CHARGE_ZERO_PAYOUT,
  ERROR_CODE_EMAIL_TAKEN,
  ERROR_CODE_EMAIL_NOT_FOUND,
  ERROR_CODE_TOO_MANY_VERIFICATION_REQUESTS,
  ERROR_CODE_UPLOAD_OVER_LIMIT,
  ERROR_CODE_VALIDATION_INVALID_PARAMS,
  ERROR_CODE_VALIDATION_INVALID_VALUE,
  ERROR_CODE_NOT_FOUND,
  ERROR_CODE_FORBIDDEN,
  ERROR_CODE_MISSING_STRIPE_ACCOUNT,
  ERROR_CODE_STOCK_OLD_TOTAL_MISMATCH,
  ERROR_CODE_PERMISSION_DENIED_POST_LISTINGS,
  ERROR_CODE_PERMISSION_DENIED_PENDING_APPROVAL,
  ERROR_CODE_USER_PENDING_APPROVAL,
  ERROR_CODE_PERMISSION_DENIED_INITIATE_TRANSACTIONS,
  ERROR_CODE_PERMISSION_DENIED_READ,
];

// API error
propTypes.apiError = shape({
  id: propTypes.uuid.isRequired,
  status: number.isRequired,
  code: oneOf(ERROR_CODES).isRequired,
  title: string.isRequired,
  meta: object,
});

propTypes.assetDeliveryApiError = shape({
  code: oneOf(ERROR_CODES).isRequired,
  id: string.isRequired,
  status: number.isRequired,
  title: string.isRequired,
});

// Storable error prop type. (Error object should not be stored as it is.)
propTypes.error = shape({
  type: propTypes.value('error').isRequired,
  name: string.isRequired,
  message: string,
  status: number,
  statusText: string,
  apiErrors: arrayOf(oneOfType([propTypes.apiError, propTypes.assetDeliveryApiError])),
});

// Options for showing just date or date and time on TimeRange and OrderBreakdown
export const DATE_TYPE_DATE = 'date';
export const DATE_TYPE_TIME = 'time';
export const DATE_TYPE_DATETIME = 'datetime';

export const STRIPE_ONBOARDING_RETURN_URL_SUCCESS = 'success';
export const STRIPE_ONBOARDING_RETURN_URL_FAILURE = 'failure';
export const STRIPE_ONBOARDING_RETURN_URL_RESTRICTED = 'restricted';

export const USER_TYPE_VERIFIED = 'verified';
export const USER_TYPE_UNVERIFIED = 'unverified';
export const USER_TYPE_RESTRICTED = 'restricted_due_to_failed_attempts';
export const USER_TYPE_MANUAL_OVERRIDE = 'manual_override';

// extra signup steps
export const SIGNUP_TAB_DETAIL = 'detail';
export const SignUpWelcomeGroups = [
  {
    label: 'Group Title1',
    option: 'Group Title1',
    subTitle: 'Group is explained here in a sentence or two so users know...',
    image:
      'https://res.cloudinary.com/turnouthq-dev/image/upload/v1733126177/674d6819-2fad-4427-858d-f902926fb0b1.png',
  },
  {
    label: 'Group Title2',
    option: 'Group Title2',
    subTitle: 'Group is explained here in a sentence or two so users know...',
    image:
      'https://res.cloudinary.com/turnouthq-dev/image/upload/v1733126177/674d6819-2fad-4427-858d-f902926fb0b1.png',
  },
  {
    label: 'Group Title3',
    option: 'Group Title3',
    subTitle: 'Group is explained here in a sentence or two so users know...',
    image:
      'https://res.cloudinary.com/turnouthq-dev/image/upload/v1733126177/674d6819-2fad-4427-858d-f902926fb0b1.png',
  },
  {
    label: 'Group Title4',
    option: 'Group Title4',
    subTitle: 'Group is explained here in a sentence or two so users know...',
    image:
      'https://res.cloudinary.com/turnouthq-dev/image/upload/v1733126177/674d6819-2fad-4427-858d-f902926fb0b1.png',
  },
  {
    label: 'Group Title5',
    option: 'Group Title5',
    subTitle: 'Group is explained here in a sentence or two so users know...',
    image:
      'https://res.cloudinary.com/turnouthq-dev/image/upload/v1733126177/674d6819-2fad-4427-858d-f902926fb0b1.png',
  },
];

export const TopbarCommunityDropdown = [
  {
    type: 'internal',
    href: '/p/about',
    text: 'About Us',
    group: 'secondary',
    route: {
      name: 'CMSPage',
      params: {
        pageId: 'about',
      },
      to: {
        search: '',
        hash: '',
      },
    },
  },
];

export const TopbarMarketPlaceDropdown = [
  {
    type: 'internal',
    href: '/p/about',
    text: 'About Us',
    group: 'secondary',
    route: {
      name: 'CMSPage',
      params: {
        pageId: 'about',
      },
      to: {
        search: '',
        hash: '',
      },
    },
  },
];

export const heroSectionType = [
  // { value: 'marketplace', label: 'Marketplace' },
  { value: 'community', label: 'Community' },
];

export const userInterests = [
  {
    label: 'Dressage',
    option: 'Dressage',
  },
  {
    label: 'Show Jumping',
    option: 'Show Jumping',
  },
  {
    label: 'Eventing (Three-Day Eventing)',
    option: 'Eventing (Three-Day Eventing)',
  },
  {
    label: 'Cross-Country Riding',
    option: 'Cross-Country Riding',
  },
  {
    label: 'Endurance Riding',
    option: 'Endurance Riding',
  },
  {
    label: 'Hunter/Jumper Competitions',
    option: 'Hunter/Jumper Competitions',
  },
  {
    label: 'Western Pleasure',
    option: 'Western Pleasure',
  },
  {
    label: 'Reining',
    option: 'Reining',
  },
  {
    label: 'Barrel Racing',
    option: 'Barrel Racing',
  },
  {
    label: 'Pole Bending',
    option: 'Pole Bending',
  },
  {
    label: 'Cutting',
    option: 'Cutting',
  },
  {
    label: 'Roping',
    option: 'Roping',
  },
  {
    label: 'Team Penning',
    option: 'Team Penning',
  },
  {
    label: 'Ranch Sorting',
    option: 'Ranch Sorting',
  },
  {
    label: 'Trail Riding',
    option: 'Trail Riding',
  },
  {
    label: 'Mounted Archery',
    option: 'Mounted Archery',
  },
  {
    label: 'Mounted Shooting',
    option: 'Mounted Shooting',
  },
  {
    label: 'Driving (Single or Multiple Horse Teams)',
    option: 'Driving (Single or Multiple Horse Teams)',
  },
  {
    label: 'Carriage Driving',
    option: 'Carriage Driving',
  },
  {
    label: 'Combined Driving',
    option: 'Combined Driving',
  },
  {
    label: 'Fox Hunting',
    option: 'Fox Hunting',
  },
  {
    label: 'Polo',
    option: 'Polo',
  },
  {
    label: 'Vaulting',
    option: 'Vaulting',
  },
  {
    label: 'Gymkhana',
    option: 'Gymkhana',
  },
  {
    label: 'Horsemanship Classes',
    option: 'Horsemanship Classes',
  },
  {
    label: 'Saddle Seat Riding',
    option: 'Saddle Seat Riding',
  },
  {
    label: 'Pleasure Driving',
    option: 'Pleasure Driving',
  },
  {
    label: 'Hunter Under Saddle',
    option: 'Hunter Under Saddle',
  },
  {
    label: 'Equitation (Hunt Seat, Western, Saddle Seat)',
    option: 'Equitation (Hunt Seat, Western, Saddle Seat)',
  },
  {
    label: 'Halter Classes',
    option: 'Halter Classes',
  },
  {
    label: 'Showmanship',
    option: 'Showmanship',
  },
  {
    label: 'Liberty Classes',
    option: 'Liberty Classes',
  },
  {
    label: 'Leadline Classes',
    option: 'Leadline Classes',
  },
  {
    label: 'Dressage Musical Freestyle',
    option: 'Dressage Musical Freestyle',
  },
  {
    label: 'Freestyle Reining',
    option: 'Freestyle Reining',
  },
  {
    label: 'Horseback Camping',
    option: 'Horseback Camping',
  },
  {
    label: 'Horseback Picnics',
    option: 'Horseback Picnics',
  },
  {
    label: 'Therapeutic Riding',
    option: 'Therapeutic Riding',
  },
  {
    label: 'Horseback Riding Lessons',
    option: 'Horseback Riding Lessons',
  },
  {
    label: 'Pony Club Activities',
    option: 'Pony Club Activities',
  },
  {
    label: 'Riding Clinics',
    option: 'Riding Clinics',
  },
  {
    label: 'Horseback Safari',
    option: 'Horseback Safari',
  },
  {
    label: 'Horse Breeding',
    option: 'Horse Breeding',
  },
  {
    label: 'Horse Training',
    option: 'Horse Training',
  },
  {
    label: 'Horse Racing',
    option: 'Horse Racing',
  },
  {
    label: 'Steeplechase',
    option: 'Steeplechase',
  },
  {
    label: 'Harness Racing',
    option: 'Harness Racing',
  },
  {
    label: 'Racing Retraining Programs',
    option: 'Racing Retraining Programs',
  },
  {
    label: 'Horse Photography',
    option: 'Horse Photography',
  },
  {
    label: 'Horseback Yoga',
    option: 'Horseback Yoga',
  },
  {
    label: 'Horse Shows and Exhibitions',
    option: 'Horse Shows and Exhibitions',
  },
  {
    label: 'Equine Therapy',
    option: 'Equine Therapy',
  },
  {
    label: 'Equine-Assisted Learning',
    option: 'Equine-Assisted Learning',
  },
  {
    label: 'Equine Health and Veterinary Care',
    option: 'Equine Health and Veterinary Care',
  },
  {
    label: 'Tack Cleaning and Maintenance',
    option: 'Tack Cleaning and Maintenance',
  },
  {
    label: 'Horse Grooming',
    option: 'Horse Grooming',
  },
  {
    label: 'Farriery (Horse Shoeing)',
    option: 'Farriery (Horse Shoeing)',
  },
  {
    label: 'Horse Auctions',
    option: 'Horse Auctions',
  },
  {
    label: 'Breed Registrations and Shows',
    option: 'Breed Registrations and Shows',
  },
  {
    label: 'Horse Breeding Clinics',
    option: 'Horse Breeding Clinics',
  },
  {
    label: 'Equine Nutrition',
    option: 'Equine Nutrition',
  },
  {
    label: 'Stable Management',
    option: 'Stable Management',
  },
  {
    label: 'Horseback Parades',
    option: 'Horseback Parades',
  },
  {
    label: 'Mounted Patrol',
    option: 'Mounted Patrol',
  },
  {
    label: 'Horse Riding Camps',
    option: 'Horse Riding Camps',
  },
];

export const userHorseBreeds = [
  {
    label: 'American Quarter Horse',
    option: 'American Quarter Horse',
  },
  {
    label: 'American Saddlebred',
    option: 'American Saddlebred',
  },
  {
    label: 'Appaloosa',
    option: 'Appaloosa',
  },
  {
    label: 'Andalusian',
    option: 'Andalusian',
  },
  {
    label: 'Arabian',
    option: 'Arabian',
  },
  {
    label: 'Icelandic Horse',
    option: 'Icelandic Horse',
  },
  {
    label: 'Canadian Horse',
    option: 'Canadian Horse',
  },
  {
    label: 'Australian Stock Horse',
    option: 'Australian Stock Horse',
  },
  {
    label: 'Belgian Draft',
    option: 'Belgian Draft',
  },
  {
    label: 'Buckskin',
    option: 'Buckskin',
  },
  {
    label: 'Chincoteague Pony',
    option: 'Chincoteague Pony',
  },
  {
    label: 'Cleveland Bay',
    option: 'Cleveland Bay',
  },
  {
    label: 'Clydesdale',
    option: 'Clydesdale',
  },
  {
    label: 'Irish Draught',
    option: 'Irish Draught',
  },
  {
    label: 'Criollo',
    option: 'Criollo',
  },
  {
    label: 'Dales Pony',
    option: 'Dales Pony',
  },
  {
    label: 'Dartmoor Pony',
    option: 'Dartmoor Pony',
  },
  {
    label: 'Dutch Warmblood',
    option: 'Dutch Warmblood',
  },
  {
    label: 'Missouri Fox Trotter',
    option: 'Missouri Fox Trotter',
  },
  {
    label: 'Fell Pony',
    option: 'Fell Pony',
  },
  {
    label: 'Fjord Horse',
    option: 'Fjord Horse',
  },
  {
    label: 'Friesian',
    option: 'Friesian',
  },
  {
    label: 'Gypsy Vanner',
    option: 'Gypsy Vanner',
  },
  {
    label: 'Hackney Horse',
    option: 'Hackney Horse',
  },
  {
    label: 'Haflinger',
    option: 'Haflinger',
  },
  {
    label: 'Hanoverian',
    option: 'Hanoverian',
  },
  {
    label: 'Holsteiner',
    option: 'Holsteiner',
  },
  {
    label: 'Mustang',
    option: 'Mustang',
  },
  {
    label: 'Knabstrupper',
    option: 'Knabstrupper',
  },
  {
    label: 'Lipizzaner',
    option: 'Lipizzaner',
  },
  {
    label: 'Lusitano',
    option: 'Lusitano',
  },
  {
    label: 'Morgan',
    option: 'Morgan',
  },
  {
    label: 'Oldenburg',
    option: 'Oldenburg',
  },
  {
    label: 'Palomino',
    option: 'Palomino',
  },
  {
    label: 'Paint Horse',
    option: 'Paint Horse',
  },
  {
    label: 'Paso Fino',
    option: 'Paso Fino',
  },
  {
    label: 'Percheron',
    option: 'Percheron',
  },
  {
    label: 'Roan',
    option: 'Roan',
  },
  {
    label: 'Rocky Mountain Horse',
    option: 'Rocky Mountain Horse',
  },
  {
    label: 'Pinto',
    option: 'Pinto',
  },
  {
    label: 'Shetland Pony',
    option: 'Shetland Pony',
  },
  {
    label: 'Shire',
    option: 'Shire',
  },
  {
    label: 'Standardbred',
    option: 'Standardbred',
  },
  {
    label: 'Suffolk Punch',
    option: 'Suffolk Punch',
  },
  {
    label: 'Tennessee Walking Horse',
    option: 'Tennessee Walking Horse',
  },
  {
    label: 'Thoroughbred',
    option: 'Thoroughbred',
  },
  {
    label: 'Welsh Pony',
    option: 'Welsh Pony',
  },
  {
    label: 'Miniature Horse',
    option: 'Miniature Horse',
  },
  {
    label: 'Other',
    option: 'Other',
  },
];

export const userHorseAssociations = [
  {
    label: 'American Driving Society (ADS)',
    option: 'American Driving Society (ADS)',
  },
  {
    label: 'American Endurance Ride Conference (AERC)',
    option: 'American Endurance Ride Conference (AERC)',
  },
  {
    label: 'American Haflinger Registry (AHR)',
    option: 'American Haflinger Registry (AHR)',
  },
  {
    label: 'American Horse Council (AHC)',
    option: 'American Horse Council (AHC)',
  },
  {
    label: 'American Morgan Horse Association (AMHA)',
    option: 'American Morgan Horse Association (AMHA)',
  },
  {
    label: 'American Mule Association (AMA)',
    option: 'American Mule Association (AMA)',
  },
  {
    label: 'American Paint Horse Association (APHA)',
    option: 'American Paint Horse Association (APHA)',
  },
  {
    label: 'American Polocrosse Association (APA)',
    option: 'American Polocrosse Association (APA)',
  },
  {
    label: 'American Quarter Horse Association (AQHA)',
    option: 'American Quarter Horse Association (AQHA)',
  },
  {
    label: 'American Riding Instructors Association (ARIA)',
    option: 'American Riding Instructors Association (ARIA)',
  },
  {
    label: 'American Saddlebred Horse Association (ASHA)',
    option: 'American Saddlebred Horse Association (ASHA)',
  },
  {
    label: 'American Saddlebred Registry (ASR)',
    option: 'American Saddlebred Registry (ASR)',
  },
  {
    label: 'American Vaulting Association (AVA)',
    option: 'American Vaulting Association (AVA)',
  },
  {
    label: 'Appaloosa Horse Club (ApHC)',
    option: 'Appaloosa Horse Club (ApHC)',
  },
  {
    label: 'Arabian Horse Association (AHA)',
    option: 'Arabian Horse Association (AHA)',
  },
  {
    label: 'Friesian Horse Association of North America (FHANA)',
    option: 'Friesian Horse Association of North America (FHANA)',
  },
  {
    label: 'Gypsy Vanner Horse Society (GVHS)',
    option: 'Gypsy Vanner Horse Society (GVHS)',
  },
  {
    label: 'International Andalusian and Lusitano Horse Association (IALHA)',
    option: 'International Andalusian and Lusitano Horse Association (IALHA)',
  },
  {
    label: 'International Mountain Trail Challenge Association (IMTCA)',
    option: 'International Mountain Trail Challenge Association (IMTCA)',
  },
  {
    label: 'National Barrel Horse Association (NBHA)',
    option: 'National Barrel Horse Association (NBHA)',
  },
  {
    label: 'National Cutting Horse Association (NCHA)',
    option: 'National Cutting Horse Association (NCHA)',
  },
  {
    label: 'National Reining Horse Association (NRHA)',
    option: 'National Reining Horse Association (NRHA)',
  },
  {
    label: 'National Steeplechase Association (NSA)',
    option: 'National Steeplechase Association (NSA)',
  },
  {
    label: 'Paso Fino Horse Association (PFHA)',
    option: 'Paso Fino Horse Association (PFHA)',
  },
  {
    label: 'Percheron Horse Association of America (PHAOA)',
    option: 'Percheron Horse Association of America (PHAOA)',
  },
  {
    label: 'Rocky Mountain Horse Association (RMHA)',
    option: 'Rocky Mountain Horse Association (RMHA)',
  },
  {
    label: 'The Jockey Club',
    option: 'The Jockey Club',
  },
  {
    label: 'United States Dressage Federation (USDF)',
    option: 'United States Dressage Federation (USDF)',
  },
  {
    label: 'United States Equestrian Federation (USEF)',
    option: 'United States Equestrian Federation (USEF)',
  },
  {
    label: 'United States Eventing Association (USEA)',
    option: 'United States Eventing Association (USEA)',
  },
  {
    label: 'United States Hunter Jumper Association (USHJA)',
    option: 'United States Hunter Jumper Association (USHJA)',
  },
  {
    label: 'United States Polo Association (USPA)',
    option: 'United States Polo Association (USPA)',
  },
  {
    label: 'United States Pony Clubs (USPC)',
    option: 'United States Pony Clubs (USPC)',
  },
  {
    label: 'Western Dressage Association of America (WDAA)',
    option: 'Western Dressage Association of America (WDAA)',
  },
  {
    label: 'International Federation for Equestrian Sports (FEI) – USA Affiliate',
    option: 'International Federation for Equestrian Sports (FEI) – USA Affiliate',
  },
  {
    label: 'North American Trail Ride Conference (NATRC)',
    option: 'North American Trail Ride Conference (NATRC)',
  },
  {
    label: 'Professional Rodeo Cowboys Association (PRCA)',
    option: 'Professional Rodeo Cowboys Association (PRCA)',
  },
  {
    label: 'Professional Association of Therapeutic Horsemanship (PATH) International',
    option: 'Professional Association of Therapeutic Horsemanship (PATH) International',
  },
  {
    label: 'American Warmblood Society & Sporthorse Registry (AWSSR)',
    option: 'American Warmblood Society & Sporthorse Registry (AWSSR)',
  },
  {
    label: 'Pinto Horse Association (PtHA)',
    option: 'Pinto Horse Association (PtHA)',
  },
  {
    label: 'American Buckskin Horse Association (ABRA)',
    option: 'American Buckskin Horse Association (ABRA)',
  },
  {
    label: 'Palomino Horse Breeders of America (PHBA)',
    option: 'Palomino Horse Breeders of America (PHBA)',
  },
  {
    label: 'The American Rodeo',
    option: 'The American Rodeo',
  },
  {
    label: 'Pro Rodeo Association (PRCA)',
    option: 'Pro Rodeo Association (PRCA)',
  },
  {
    label: "Women's Professional Rodeo Association (WPRA)",
    option: "Women's Professional Rodeo Association (WPRA)",
  },
  {
    label: 'Indian National Finals Rodeo (INFR)',
    option: 'Indian National Finals Rodeo (INFR)',
  },
  {
    label: 'International Pro Rodeo Association (IRA)',
    option: 'International Pro Rodeo Association (IRA)',
  },
  {
    label: 'Pro Rodeo Canada Association (CPRA)',
    option: 'Pro Rodeo Canada Association (CPRA)',
  },
  {
    label: 'College National Finals Rodeo (CNFR)',
    option: 'College National Finals Rodeo (CNFR)',
  },
  {
    label: 'Bill Pickett Invitational Rodeo',
    option: 'Bill Pickett Invitational Rodeo',
  },
  {
    label: 'Professional Bull Riders',
    option: 'Professional Bull Riders',
  },
  {
    label: 'National Collegiate Equestrian Association (NCEA)',
    option: 'National Collegiate Equestrian Association (NCEA)',
  },
  {
    label: 'Intercollegiate Horse Shows Association (IHSA)', // Intercollegiate Horse Shows Association (IHSA)
    option: 'International Horse Show Association (IHSA)',
  },
  {
    label:"Interscholastic Equestrian Association (IEA)",
    option:"Interscholastic Equestrian Association (IEA)"
  },
  {
    label: 'Youth Equestrian Development Association (YEDA)',
    option: 'Youth Equestrian Development Association (YEDA)',
  },
];

export const customUserFields = [
  {
    key: 'shippingAddress',
    scope: 'public',
    schemaType: SCHEMA_TYPE_ADDRESS,
    showConfig: {
      label: 'Ship from address',
      displayInProfile: true,
      unselectedOptions: true,
    },
    saveConfig: {
      label: 'Ship from address',
      isRequired: false,
      displayInSignUp: true,
      placeholderMessage: 'Where you are shipping items from?',
      requiredMessage: 'Location field is required.',
      addressNotRecognizedMessage: "We didn't get your entered location.",
    },
    userTypeConfig: {
      limitToUserTypeIds: false,
    },
  },
  {
    key: 'pickupAddress',
    scope: 'public',
    schemaType: SCHEMA_TYPE_ADDRESS,
    showConfig: {
      label: 'Pickup area',
      displayInProfile: true,
      unselectedOptions: true,
    },
    saveConfig: {
      label: 'Pickup area',
      isRequired: false,
      displayInSignUp: true,
      placeholderMessage: 'Where will pickup’s take place?',
      requiredMessage: 'Location Field is Required.',
      addressNotRecognizedMessage: "We didn't get your entered location.",
    },
    userTypeConfig: {
      limitToUserTypeIds: false,
    },
  },
  {
    key: 'interests',
    scope: 'public',
    schemaType: SCHEMA_TYPE_MULTI_SELECT_CREATE_ENUM,
    enumOptions: userInterests,
    showConfig: {
      label: 'My Interests',
      displayInProfile: true,
      unselectedOptions: true,
    },
    saveConfig: {
      label: 'My Interests',
      isRequired: false,
      displayInSignUp: false,
      requiredMessage: 'Interests Field is Required',
      placeholderMessage: 'Select or add new interests',
    },
    userTypeConfig: {
      limitToUserTypeIds: false,
    },
  },
  {
    key: 'horseBreeds',
    scope: 'public',
    schemaType: SCHEMA_TYPE_MULTI_SELECT_CREATE_ENUM,
    enumOptions: userHorseBreeds,
    showConfig: {
      label: 'Horse Breeds',
      displayInProfile: true,
      unselectedOptions: true,
    },
    saveConfig: {
      label: 'Horse Breeds',
      isRequired: false,
      displayInSignUp: false,
      placeholderMessage: 'Select or add new horse breeds',
      requiredMessage: 'Horse Breeds Field is Required',
    },
    userTypeConfig: {
      limitToUserTypeIds: false,
    },
  },
];

propTypes.dateType = oneOf([DATE_TYPE_DATE, DATE_TYPE_TIME, DATE_TYPE_DATETIME]);


export const CURRENT_USER_INDIVIDUAL_TYPE = "I am an individual";
export const CURRENT_USER_BUSINESS_TYPE = "I am a business";

export const CURRENT_USER_TYPE_BUSINESS_LABEL = "I am a business / nonprofit";

export const CURRENT_USER_INDIVIDUAL_TYPE_LABEL = "Individual";
export const CURRENT_USER_BUSINESS_TYPE_LABEL = "Business";

export const PROFILE_LISTING_TYPE = "profile_listing";
export const currentUserTypes = [
  { label: CURRENT_USER_INDIVIDUAL_TYPE, key: CURRENT_USER_INDIVIDUAL_TYPE },
  { label: CURRENT_USER_TYPE_BUSINESS_LABEL, key: CURRENT_USER_BUSINESS_TYPE }
];

// user types
export const USER_TYPE_ENTRY = 'entry';
export const USER_TYPE_YOUTH = 'youth';
export const USER_TYPE_BASIC = 'basic';
export const ALL_USER_TYPES = [USER_TYPE_ENTRY, USER_TYPE_YOUTH, USER_TYPE_BASIC];

// user permissions
export const stripeVerification = 'stripe_verification';
export const connectedAccount = 'connected_account';
export const yearlySubscription = 'yearly_subscription';
export const browsingMarketplace = 'browsing_marketplace';
export const abilityToBuy = 'ability_to_buy';
export const abilityToSellUnder500 = 'ability_to_sell_under_500';
export const abilityToSellOver500 = 'ability_to_sell_over_500';
export const abilityToSell = 'ability_to_sell';
export const communityAccess = 'community_access';
export const communityPostAccess = 'community_post_access';
export const communityGorupAccess = 'community_group_access';
export const standardOverrideRake = 'standard_override_rake';
export const annualSubscriptionDiscountsAnualRate = 'annual_subscription_discounts_anual_rate';
export const annualSubscriptionDiscountsStandardRate =
  'annual_subscription_discounts_standard_rate';

export const sellerRakeCommissionUnder100 = 'seller_rake_commission_under_100';
export const sellerRakeCommissionUnder500 = 'seller_rake_commission_under_500';
export const sellerRakeCommissionUnder1000 = 'seller_rake_commission_under_1000';
export const sellerRakeCommissionOver1000 = 'seller_rake_commission_over_1000';

export const buyerRakeCommissionUnder100 = 'seller_rake_commission_under_100';
export const buyerRakeCommissionUnder500 = 'seller_rake_commission_under_500';
export const buyerRakeCommissionOver500 = 'seller_rake_commission_over_500';

export const clickOnDocumnets = 'click_on_document';

export const seeGroupPaymentsList = "see_group_payments_list";
export const makeGroupPayments = "make_group_payments";


export const inquiryTab = 'inquiry';
export const OffersTab = 'offers';
export const shopifyCheckoutOrdersTab = 'shopify-checkout-orders';

export const UserPermissions = {
  // Stripe Verification
  [stripeVerification]: {
    [USER_TYPE_ENTRY]: false,
    [USER_TYPE_YOUTH]: false,
    [USER_TYPE_BASIC]: true,
  },
  // Connected Account (Adult)
  [connectedAccount]: {
    [USER_TYPE_ENTRY]: false,
    [USER_TYPE_YOUTH]: true,
    [USER_TYPE_BASIC]: false,
  },
  // Yearly Subscription
  [yearlySubscription]: {
    [USER_TYPE_ENTRY]: 0,
    [USER_TYPE_YOUTH]: 0,
    [USER_TYPE_BASIC]: 500,
  },
  // Browsing (Marketplace Only)
  [browsingMarketplace]: {
    [USER_TYPE_ENTRY]: true,
    [USER_TYPE_YOUTH]: true,
    [USER_TYPE_BASIC]: true,
  },

  // Ability to Buy
  [abilityToBuy]: {
    [USER_TYPE_ENTRY]: false,
    [USER_TYPE_YOUTH]: false,
    [USER_TYPE_BASIC]: true,
  },

  // Ability to Sell
  [abilityToSellUnder500]: {
    [USER_TYPE_ENTRY]: false,
    [USER_TYPE_YOUTH]: false,
    [USER_TYPE_BASIC]: true,
  },

  // Ability to Sell
  [abilityToSell]: {
    [USER_TYPE_ENTRY]: false,
    [USER_TYPE_YOUTH]: false,
    [USER_TYPE_BASIC]: true,
  },

  // Ability to Sell
  [abilityToSellOver500]: {
    [USER_TYPE_ENTRY]: false,
    [USER_TYPE_YOUTH]: false,
    [USER_TYPE_BASIC]: true,
  },

  // Community Access (Read Only)
  [communityAccess]: {
    [USER_TYPE_ENTRY]: true,
    [USER_TYPE_YOUTH]: true,
    [USER_TYPE_BASIC]: true,
  },

  // Community Access (Engage / Post)
  [communityPostAccess]: {
    [USER_TYPE_ENTRY]: true,
    [USER_TYPE_YOUTH]: true,
    [USER_TYPE_BASIC]: true,
  },

  // Community Access (Request Group - Requires Paid Membership)
  [communityGorupAccess]: {
    [USER_TYPE_ENTRY]: false,
    [USER_TYPE_YOUTH]: false,
    [USER_TYPE_BASIC]: true,
  },

  // Standard Override Rake
  [standardOverrideRake]: {
    [USER_TYPE_ENTRY]: false,
    [USER_TYPE_YOUTH]: false,
    [USER_TYPE_BASIC]: false,
  },

  // Annual Subscription Discounts
  [annualSubscriptionDiscountsAnualRate]: {
    [USER_TYPE_ENTRY]: false,
    [USER_TYPE_YOUTH]: false,
    [USER_TYPE_BASIC]: false,
  },

  [annualSubscriptionDiscountsStandardRate]: {
    [USER_TYPE_ENTRY]: false,
    [USER_TYPE_YOUTH]: false,
    [USER_TYPE_BASIC]: false,
  },

  // Seller Rake / Commission
  [sellerRakeCommissionUnder100]: {
    [USER_TYPE_ENTRY]: false,
    [USER_TYPE_YOUTH]: false,
    [USER_TYPE_BASIC]: 10,
  },

  [sellerRakeCommissionUnder500]: {
    [USER_TYPE_ENTRY]: false,
    [USER_TYPE_YOUTH]: false,
    [USER_TYPE_BASIC]: 10,
  },

  [sellerRakeCommissionUnder1000]: {
    [USER_TYPE_ENTRY]: false,
    [USER_TYPE_YOUTH]: false,
    [USER_TYPE_BASIC]: 10,
  },

  [sellerRakeCommissionOver1000]: {
    [USER_TYPE_ENTRY]: false,
    [USER_TYPE_YOUTH]: false,
    [USER_TYPE_BASIC]: 4,
  },

  // Buyer Rake / Commission
  [buyerRakeCommissionUnder100]: {
    [USER_TYPE_ENTRY]: false,
    [USER_TYPE_YOUTH]: false,
    [USER_TYPE_BASIC]: false,
  },

  [buyerRakeCommissionUnder500]: {
    [USER_TYPE_ENTRY]: false,
    [USER_TYPE_YOUTH]: false,
    [USER_TYPE_BASIC]: false,
  },

  [buyerRakeCommissionOver500]: {
    [USER_TYPE_ENTRY]: false,
    [USER_TYPE_YOUTH]: false,
    [USER_TYPE_BASIC]: false,
  },

  [clickOnDocumnets]: {
    [USER_TYPE_ENTRY]: true,
    [USER_TYPE_YOUTH]: true,
    [USER_TYPE_BASIC]: true,
  },

  [seeGroupPaymentsList]: {
    [USER_TYPE_ENTRY]: true,
    [USER_TYPE_YOUTH]: true,
    [USER_TYPE_BASIC]: true,
  },

  [makeGroupPayments]: {
    [USER_TYPE_ENTRY]: true,
    [USER_TYPE_YOUTH]: false,
    [USER_TYPE_BASIC]: true,
  }

};

export const SUBSCRIPTION_STATUS_ACTIVE = 'active';
export const SUBSCRIPTION_STATUS_TRAILLED = 'trialing';
export const SUBSCRIPTION_STATUS_CANCELLED = 'canceled';
export const SUBSCRIPTION_STATUS_UNPAID = 'unpaid';
export const SUBSCRIPTION_STATUS_INCOMPLETE = 'incomplete';
export const SUBSCRIPTION_STATUS_INCOMPLETE_EXPIRED = 'incomplete_expired';
export const SUBSCRIPTION_STATUS_PAST_DUE = 'past_due';
export const ALL_SUBSCRIPTION_STATUS = [
  SUBSCRIPTION_STATUS_INCOMPLETE,
  SUBSCRIPTION_STATUS_INCOMPLETE_EXPIRED,
  SUBSCRIPTION_STATUS_TRAILLED,
  SUBSCRIPTION_STATUS_ACTIVE,
  SUBSCRIPTION_STATUS_PAST_DUE,
  SUBSCRIPTION_STATUS_CANCELLED,
  SUBSCRIPTION_STATUS_UNPAID,
];

export const nonVerifyEamilRestrictedRoutes = [
  "CircleCommunityPage",
  "ExploreCommunityPage",
  "MyGroupCommunityPage",
  "DiscoverGroupsCommunityPage",
  "JoinedGroupsCommunityPage",
  "PostViewPage",
  "GroupSearchPage",
  "CheckoutPage",
  "CartPage",
  "EditListingPage",
  "ProfilePage",
  "ProfileSettingsPage",
  "InboxPage",
  "OrderDetailsPage",
  "SaleDetailsPage",
  "ManageListingsPage",
  "PasswordChangePage",
  "DeleteAccountPage",
  "ShippingInfoPage",
  "YouthAccountSettingsPage",
  "StripePayoutPage",
  "StripePayoutOnboardingPage",
  "PaymentMethodsPage",
  "GroupPaymentHistoryPage",
  "StripeSubscriptionPage",
  "CartPage",
  "SearchPage",
  "AlgoliaDirectorySearchPage",
  // "AdminPanelSupabasePage",
  // "AdminPanelChatHistoryPage",
  // "AdminPanelOrganisationPage",
  // "AdminPanelWalletPage",
  // "AdminPanelModeratorsPage",
  // "AiChatBotPage",
  // "AiChatBotRulebookPage",
  // "RuleBooksSearchPage",
  "FavouritePage"
];
export const communityInviiteRoute = [
  "MyGroupCommunityPage",
  "MyGroupCommunityPageForUnknownUser",
  "CircleCommunityPage",
];

export const upcomingRoutes = [
  // 'CartPage',
  // 'ManageListingsPage',
  // 'SearchPage',
  // "StripePayoutPage",
  // 'ListingPage',
  // 'CheckoutPage',
  // 'ListingPageVariant',
  // 'EditListingPage',
  // 'InboxPage',
  // 'OrderDetailsPage',
  // 'SaleDetailsPage',
  //  "ProfilePage",
  //  "ShippingInfoPage"
];

export const restrictedValidRoutes = [
  // 'CartPage',
  // 'ManageListingsPage',
  // 'SearchPage',
  // 'ListingPage',
  // 'CheckoutPage',
  // 'ListingPageVariant',
  // 'EditListingPage',
  // 'InboxPage',
  // 'OrderDetailsPage',
  // 'SaleDetailsPage',
  // "ProfilePage",
  // "ShippingInfoPage",
  // "AdminPanelSupabasePage",
  // "AiChatBotPage",
  // "AiChatBotRulebookPage",
  // "RuleBooksSearchPage"
];

// sendGrid Template Ids
export const REQUEST_GROUP_TEMPLATE_ID = 'd-cbf12939c003420ca34b5dda51aa23de';
export const REQUEST_JOIN_GROUP_TEMPLATE_ID = 'd-a68ea6f5d1db4c9e83e12d8d9ab20afd';
export const REQUEST_GROUP_PREGRESS = 'request_group';
export const STRIPE_VERIFICATION_ACCEPTED_TEMPLATE_ID = 'd-7635debefc02416893482c3d0f8569df';
export const STRIPE_VERIFICATION_DENIED_TEMPLATE_ID = 'd-9cf1b1bbad564dbe93c4f03f701d1cff';
export const STRIPE_VERIFICATION_LIMIT_REACHED_TEMPLATE_ID = 'd-fb94f05a54f14c7baa6663495a942fe2';
export const STRIPE_VERIFICATION_LIMIT_REACHED_ADMIN_TEMPLATE_ID = 'd-cc6efef7e93a4feda6381bde8de0c58d';

export const REQUEST_RULEBOOK_TEMPLATE_ID = 'd-04552a30875e4f549e3a17d50bf8e995';

export const GROUP_PAYMENT_CREATE_TEMPLATE_ID = "d-535b263d1f564b5f967d074278bb3a69";
export const GROUP_PAYMENT_CONFIRM_TEMPLATE_ID = "d-82c524c8ff534fb0af82a43663f31108";
export const GROUP_PAYMENT_SUBSCRIPTION_INVOICE_TEMPLATE_ID = "d-a0b9b6b74d2d41e58f6c98c694c3c5e5";
export const GROUP_PAYMENT_RECURRING_CANCELLED_INVOICE_TEMPLATE_ID = "d-6c2ccfe31350429ab1e782a85c8cbbe0";
export const INVITE_CONNECTED_ACCOUNT_TEMPLATE_ID = "d-a62983ed16e6448daa6754664d07123d";


export const LOCAL_STORAGE_CIRCLE_AUTH_TOKEN = 'circleAuth';


// Feed Tabs
export const FEED_TAB = "FEED";
export const EXPLORE = "EXPLORE";
export const SAVED_POST = "SAVED_POST";
export const MARKETPLACE = "MARKETPLACE";
export const NOTIFICATION = 'NOTIFICATION';
export const ACTIVITY = 'MY_ACTIVITY';
// My Activity Tabs
export const POSTS = "POSTS";
export const COMMENTS = "COMMENTS";

// Group page tabs
export const GROUP_PAGE_TAB_POSTS = 'posts';
export const GROUP_PAGE_TAB_HIGHLIGHTS = 'highlights';
export const GROUP_PAGE_TAB_ABOUT = 'about';
export const GROUP_PAGE_TAB_DOCUMENTS = 'documents';
export const GROUP_PAGE_TAB_PAYMENTS = 'payments';
export const GROUP_PAGE_TAB_MANAGE_MEMBERS = 'manage-members';

export const GROUP_PAGE_VISIBLE_TABS = [
  GROUP_PAGE_TAB_POSTS,
  GROUP_PAGE_TAB_HIGHLIGHTS,
  GROUP_PAGE_TAB_ABOUT,
  GROUP_PAGE_TAB_DOCUMENTS,
  GROUP_PAGE_TAB_PAYMENTS,
];

export const GROUP_PAGE_SORT_MOST_RECENT = 'latest';
export const GROUP_PAGE_SORT_OLDEST = 'oldest';
export const GROUP_PAGE_SORT_TRENDING = 'popular';

export const feedArray = [
  {
    Icon: <ProfileIconCard type="feed" />,
    text: 'My Feed',
    tab: FEED_TAB,
    pageName: "CircleCommunityPage"
  },
  {
    Icon: <ProfileIconCard type="explore" />,
    text: 'Discover Groups',
    tab: EXPLORE,
    pageName: "GroupSearchPage"
  },
  {
    Icon: <ProfileIconCard type="saved" />,
    text: 'Saved Posts',
    tab: SAVED_POST,
    pageName: false
  },
  {
    Icon: <ProfileIconCard type="activity" />,
    text: 'My Activity',
    tab: ACTIVITY,
    pageName: false,
  },
  // {
  //   Icon: <ProfileIconCard type="marketplace" />,
  //   text: 'Marketplace',
  //   tab: MARKETPLACE,
  //   pageName: "SearchPage"
  // },
];

export const myActivityArray = [
  {
    text: 'Posts',
    tab: "posts",
    pageName: "MyActivityModal"
  },
  {
    text: 'Comments',
    tab: "comments",
    pageName: "MyActivityModal"
  },
]


export const mobileBottomTabsFeed = [
  {
    Icon: <ProfileIconCard type="marketplace_bottom_mobile_svg" />,
    tab: "MARKETPLACE",
    label: "Marketplace",
    pageName: "SearchPage",
    params: {}
  },
  {
    Icon: <ProfileIconCard type="turnoutai_bottom_mobile_svg" />,
    tab: "TURNOUTAI",
    label: "TurnoutAI",
    pageName: "RuleBooksSearchPage",
    params: { rulebookId: "all" }
  },
  {
    Icon: <ProfileIconCard type="community_bottom_mobile_svg" />,
    tab: "COMMUNITY",
    label: "Community",
    pageName: "CircleCommunityPage",
    params: {}
  },
  {
    Icon: <ProfileIconCard type="create_post_bottom_mobile_svg" />,
    tab: "ADD_MENU",
    label: "Create Post",
    pageName: "",
    params: {}
  },
  {
    Icon: <ProfileIconCard type="support_bottom_mobile_svg" />,
    tab: "SUPPORT",
    label: "Support",
    pageName: "",
    params: {}
  },
  // {
  //   Icon: <ProfileIconCard type="menu" />,
  //   tab: "MENU",
  // },
  // {
  //   Icon: <ProfileIconCard type="explore_2" />,
  //   tab: "EXPLORE",
  // },
  // {
  //   Icon: <ProfileIconCard type="add_menu" />,
  //   tab: "ADD_MENU",
  // },
  // {
  //   Icon: <ProfileIconCard type="notification" />,
  //   tab: "NOTIFICATION",
  // },
]

export const mobileBottomTabsAi = [
  {
    Icon: <ProfileIconCard type="menu" />,
    tab: "MENU",
  },
  {
    Icon: <ProfileIconCard type="explore_2" />,
    tab: "EXPLORE",
  },
  // {
  //   Icon: <ProfileIconCard type="add_menu" />,
  //   tab: "ADD_MENU",
  // },
  {
    Icon: <ProfileIconCard type="cart" />,
    tab: "NOTIFICATION",
  },
]

export const previousPriceSubcriptionIds = ["price_1QXmKoG7jpkkXwWIPNH0uDmQ"];


// mongo table names
export const MONGO_SUBSCRIPTION = 'subscription';
export const MONGO_SEQUENCE_ID = 'sequenceId';
export const MONGO_CATAGORY = "category";
export const MONGO_SUB_CATAGORY = "subCategory";
export const MONGO_SUB_SUB_CATAGORY = "subSubCategory";
export const MONGO_DYNAMIC_FIELDS_SUB_SUB_CAT = "dynamicFieldsSubSubCat";

// <<<<<<<<<<<<<<<<< algolia types >>>>>>>>>>>>>>>>>>
export const CREATE_ALGOLIA_TYPE = "createSearchData";
export const UPDATE_ALGOLIA_TYPE = "updateSearchData";
export const DELETE_ALGOLIA_TYPE = "deleteSearchData";
export const GET_ALGOLIA_TYPE = "getSearchData";
export const VIEWS_UPDATE_ALGOLIA_TYPE = "updateViews";
export const algoliaValidTypes = [GET_ALGOLIA_TYPE, CREATE_ALGOLIA_TYPE, UPDATE_ALGOLIA_TYPE, DELETE_ALGOLIA_TYPE];

// <<<<<<<<<<< ALGOLIA_SEARCH_COMPONENT >>>>>>>>>>>>>>
export const ALOGOLIA_SEARCH_COMPONENT = {
  refinement_list: "refinement_list",
  range_input: "range_input",
  menu: "menu",
  menuSelect: "menuSelect",
  selectButton: "selectButton",
  customRefinementList: "customRefinementList",
  hierarchicalList: "HierarchicalList",
  zipcode_text_field: "zipcode_text_field"
};

// <<<<<<<<<<< ALGOLIA Search Attributes>>>>>>>>>>>>>>
export const ALOGOLIA_SEARCH_ATTRIBUTES = {
  hierarchicalCategories: [
    'hierarchicalCategories.lvl0',
    'hierarchicalCategories.lvl1',
    'hierarchicalCategories.lvl2'
  ],
  categoryLevels: "categoryLevels",
  price: "price",
  color: "publicData.colorName",
  size: "publicData.sizeName",
  brand: "publicData.brandName",
  condition: "publicData.condition",
  type: "publicData.typeName",
  subCategoryName: "publicData.subCategoryName",
  pickShippingOptions: "pickShippingOptions",
  pickShippingDetailsCity: "pickShippingDetails.city",
  currentUserType:"currentUserType"
};

// hierarchicalMenu
export const SEARCH_KEY_PARAMS_HIERARCHIAL = {
  "hierarchicalCategories.lvl0": "hierarchicalCategories",
};

// hierarchicalMenu
export const ALGOLIA_SEARCH_KEY_PARAMS_HIERARCHIAL = {
  "hierarchicalCategories": "hierarchicalCategories.lvl0",
};

// refinementList
export const SEARCH_KEY_PARAMS_REFINEMENT = {
  "publicData.colorName": "color",
  "publicData.sizeName": "size",
  "publicData.brandName": "brand",
  "publicData.condition": "condition",
  "publicData.typeName": "type",
  "pickShippingOptions": "pickShippingOptions",
  "pickShippingDetails.city": "pickShippingDetailsCity",
  "categoryLevels": "categoryLevels",
  currentUserType:"currentUserType"
};

export const ALGOLIA_SEARCH_KEY_PARAMS_REFINEMENT = {
  "color": "publicData.colorName",
  "size": "publicData.sizeName",
  "brand": "publicData.brandName",
  "condition": "publicData.condition",
  "type": "publicData.typeName",
  "pickShippingOptions": "pickShippingOptions",
  "pickShippingDetailsCity": "pickShippingDetails.city",
  "categoryLevels": "categoryLevels",
  currentUserType:"currentUserType"
};

// menu
export const SEARCH_KEY_PARAMS_MENU = {
  "publicData.subCategoryName": "subCategoryName",
  currentUserType:"currentUserType"
};

export const ALGOLIA_SEARCH_KEY_PARAMS_MENU = {
  subCategoryName: "publicData.subCategoryName",
  currentUserType:"currentUserType"
};

export const mobileAlgoliaSortOptions = [
  { label: "Sort: Newest", value: REACT_APP_ALGOLIA_LISTING_INDEX, searchKeyName: "newest" },
  { label: "Sort: Oldest", value: REACT_APP_ALGOLIA_LISTING_CREATED_INDEX, searchKeyName: "oldest" },
  { label: 'Sort: Price (asc)', value: REACT_APP_ALGOLIA_LISTING_PRICE_ASC_INDEX, searchKeyName: "price-asc" },
  { label: 'Sort: Price (desc)', value: REACT_APP_ALGOLIA_LISTING_PRICE_DESC_INDEX, searchKeyName: "price-desc" },
];

export const algoliaSortOptions = [
  { label: "Newest", value: REACT_APP_ALGOLIA_LISTING_INDEX, searchKeyName: "newest" },
  { label: "Oldest", value: REACT_APP_ALGOLIA_LISTING_CREATED_INDEX, searchKeyName: "oldest" },
  { label: 'Price (asc)', value: REACT_APP_ALGOLIA_LISTING_PRICE_ASC_INDEX, searchKeyName: "price-asc" },
  { label: 'Price (desc)', value: REACT_APP_ALGOLIA_LISTING_PRICE_DESC_INDEX, searchKeyName: "price-desc" },
];

export const algoliaUsersSortOptions = [
  { label: "Newest", value: process.env.REACT_APP_ALGOLIA_USERS || "THQ_USERS", searchKeyName: "newest" },
  { label: "Oldest", value: process.env.REACT_APP_ALGOLIA_USERS_OLDEST || "THQ_USERS_OLDEST", searchKeyName: "oldest" },
]

export const LISTING_CARD_2X = "scaled-medium";
export const SHARETRIBE_TYPE_IMAGE = "image";


export const gptModals = [
  { label: "GPT-4", value: "gpt-4", },
  { label: "GPT-4 Turbo", value: "gpt-4-turbo", },
];

export const MY_FAVOURITE = "myFavourites";
export const RECENT_CHATS = "recentChats";

export { propTypes };

export const guardianChecks = [
  "I am the legal guardian of this youth.",
  "This youth is between the ages of 13 and 16.",
  "I consent to TurnoutHQ’s Terms of Service for my youth."
];

export const SIGNUP = "signup";

export const GENERAL_RULEBOOK = "general_rulebooks";
export const GENERAL_RULEBOOK_GROUP_ID = "general_rulebooks_group_id";
export const GENERAL_RULEBOOK_GROUP_NAME = "General";

export const PRIMARY_RULEBOOK_GROUP_ID = "primary_rulebooks_group_id";
export const PRIMARY_RULEBOOK_GROUP_NAME = "Primary rulebook group";

export const PRIMARY_GPT = "primary_gpt";
export const PRIMARY_GPT_EDIT = "primary_edit";
export const GENERAL_GPT = "general_gpt";
export const GENERAL_GPT_EDIT = "general_edit";

export const PRIMARY_GPT_NAME = "General";

export const UNORDER_LIST = "unordered-list-item";


// YOUTH INVITE STATUS
export const YOUTH_INVITE_STATUS = {
  CANCEL: 'cancel',
  ACCEPT: 'accept',
  INVITE: 'invite',
  SUSPEND: 'suspend'
};

export const shippingWeightTypes = [
  { key: "oz", label: "Ounces" },
  { key: "lbs", label: "Pounds" },
];

export const DEFAULT_FEED_IMAGE_URL = "https://res.cloudinary.com/turnouthq-dev/image/upload/v1747311197/Rectangle_34625791_1_1_scojvi.webp";

export const DEFAULT_FEED_IMAGE_1200_URL = "https://res.cloudinary.com/turnouthq-dev/image/upload/v1747312805/200_dpgqif.webp";

export const DEFAULT_FEED_IMAGE_600_URL = "https://res.cloudinary.com/turnouthq-dev/image/upload/v1747312890/100_g1j5w0.webp";

export const general_rulebook = "General Rulebook";
export const primary_rulebook = "Primary Rulebook";
export const group_rulebook = "Group Rulebook";
export const reported_posts = "Reported Posts";

export const rulebookTypes = [general_rulebook, primary_rulebook];

export const createTicketEventIDs = {
  "Rulebook Request": process.env.REACT_APP_INTERCOM_TICKET_RULEBOOK_REQUEST,
  "Group Request": process.env.REACT_APP_INTERCOM_TICKET_GROUP_REQUEST,
  "Order Dispute": process.env.REACT_APP_INTERCOM_TICKET_ORDER_DISPUTE,
  "High Risk User": process.env.REACT_APP_INTERCOM_TICKET_HIGH_RISK_USER,
  "Reported Posts": process.env.REACT_APP_INTERCOM_TICKET_REPORTED_POSTS,
  "Group Join Request": process.env.REACT_APP_INTERCOM_TICKET_GROUP_JOIN_REQUEST,
  "Elephant Fails": process.env.REACT_APP_INTERCOM_TICKET_ELEPHANT_FAILS
};


// Centralized Intercom event constants
export const INTERCOM_EVENTS = {
  USER_SIGNED_UP: 'user_signed_up',
};

export const PAYMENT_DASH_CREATE_PAYMENT = "create_payment";
export const PAYMENT_DASH_PAYMENT_DASHBOARD = "payment_dashboard";
export const PAYMENT_DASH_MAKE_PAYMENT = "make_payment";
export const PAYMENT_DASH_VIEW_PAYMENT_HISTORY = "view_payment_history";

export const GROUP_PAYMENT_CHARGE_FLAT_FEE = 'Flat Fee';
export const GROUP_PAYMENT_CHARGE_VARIABLE_FEE = 'Variable Fee';

export const GROUP_PAYMENT_STATUS_ACTIVE = 'active';
export const GROUP_PAYMENT_STATUS_CLOSED = 'closed';
export const GROUP_PAYMENT_STATUS_DRAFT = 'draft';
export const GROUP_PAYMENT_STATUS_DELETED = 'deleted';
export const GROUP_PAYMENT_STATUS_DISCONTINUE = 'discontinue';

export const validGroupPaymentStatuses = [GROUP_PAYMENT_STATUS_ACTIVE, GROUP_PAYMENT_STATUS_CLOSED, GROUP_PAYMENT_STATUS_DRAFT, GROUP_PAYMENT_STATUS_DISCONTINUE]

export const GROUP_PAYMENT_TYPE_MONTHLY = 'monthly';
export const GROUP_PAYMENT_TYPE_ONE_TIME = 'one-time';


export const GROUP_PAYMENT_PAGE_SCHEDULED_PAYMENTS = "scheduled_payments";
export const GROUP_PAYMENT_PAGE_PAYMENT_HISTORY = "payment_history";

export const ghostTags = [
  {
    label: 'News',
    value: 'news',
  },
];

export const YOUTH_MIN_AGE_LIMIT = 13;
export const YOUTH_AGE_LIMIT = 16;

// export const reloadIntercomOnPages= [
//   "CircleCommunityPage",
//   "DiscoverGroupsCommunityPage",
//   "GroupSearchPage",
//   "JoinedGroupsCommunityPage",
//   "MyGroupCommunityPage",
//   "PostViewPage",
//   "RuleBooksSearchPage",
//   "AiChatBotPage"
// ]

export const landingPagePublicGroupIdsToShowForLinkShare = [
  2008649,
  2031111,
  1808562,
  2082477
]
